import {
  DestroyRef,
  Injectable,
  InjectionToken,
  Signal,
  inject,
  signal,
  type WritableSignal
} from '@angular/core';
import localforage from 'localforage';

import type { GridStatePersistence } from './contracts/grid-state-persistence.interface';
import type { DataGridState } from './contracts/data-grid-state.interface';

const DEFAULT_DATA_GRID_STATE: DataGridState = {
  groupByFields: [],
  hiddenColumns: [],
  columnOrder: [],
  discriminatorColumnOrder: {},
  columnWidths: {}
};

/**
 * IndexedDB-backed grid state persistence keyed by `gridStateId`.
 * Mirrors the approach used by the Kendo `d-grid` component.
 */
@Injectable()
export class LocalForageGridStatePersistence implements GridStatePersistence {
  private readonly gridStateId: string = inject(GRID_STATE_ID);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly readySignal: WritableSignal<boolean> = signal(false);
  private readonly stateSignal: WritableSignal<DataGridState> = signal(DEFAULT_DATA_GRID_STATE);
  private storageConfigured: boolean = false;

  public readonly ready: Signal<boolean> = this.readySignal.asReadonly();
  public readonly state: Signal<DataGridState> = this.stateSignal.asReadonly();

  constructor() {
    void this.loadState();
    this.destroyRef.onDestroy((): void => {
      this.readySignal.set(false);
    });
  }

  public save(state: DataGridState): void {
    if (!this.storageConfigured) {
      this.configureStorage();
    }
    void localforage.setItem(this.gridStateId, state);
    this.stateSignal.set(state);
  }

  private configureStorage(): void {
    localforage.config({
      driver: localforage.INDEXEDDB,
      name: 'deighton-data-grid',
      storeName: 'grid-state'
    });
    this.storageConfigured = true;
  }

  private async loadState(): Promise<void> {
    this.configureStorage();
    const saved: DataGridState | null = await localforage.getItem<DataGridState>(this.gridStateId);
    if (saved !== null) {
      this.stateSignal.set(saved);
    }
    this.readySignal.set(true);
  }
}

/** Required when using {@link LocalForageGridStatePersistence}. */
export const GRID_STATE_ID: InjectionToken<string> = new InjectionToken<string>('GRID_STATE_ID');
